import React from 'react'
import type { CalendarEvent } from 'paintscout'
import { ConfirmationDialog } from '@paintscout/ui'
import { EventPreviewPopper, useClientOptions, useDialogs } from '@paintscout/ui-business'
import ProductionEventDialog from 'src/components/dialogs/EventDialog/ProductionEventDialog'
import history from 'shared/util/routing/history'
import { getCrews } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { useProductionCalendar } from './ProductionCalendarContext'
import { useProductionEventPermissions } from 'shared/hooks'

interface ProductionEventPopperProps {
  event?: CalendarEvent
  anchorEl?: HTMLElement | null
  onClose: () => void
  closeOnCrewChange?: boolean
}

export default function ProductionEventPopper({
  event,
  anchorEl,
  onClose,
  closeOnCrewChange
}: ProductionEventPopperProps) {
  const { updateEvent, deleteEvent } = useProductionCalendar()
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { permissionToEdit } = useProductionEventPermissions(event)
  const { options } = useClientOptions()

  if (!event) return null

  const handleEdit = (target: CalendarEvent) => {
    onClose()
    openDialog(ProductionEventDialog, {
      event: target,
      onCancelEvent: (eventToDelete: CalendarEvent) => {
        dismissDialog()
        handleDelete(eventToDelete)
      },
      onConfirm: (updated: CalendarEvent) => {
        updateEvent(updated)
        dismissDialog()
      }
    })
  }

  const handleCrewChange = (target: CalendarEvent, crewId: string) => {
    if (target.crew === crewId) {
      if (closeOnCrewChange) onClose()
      return
    }

    const allCrews = getCrews({ options, includeTrashed: true })
    const prevCrew = target.crew ? allCrews.find((c) => c._id === target.crew) : null
    const prevCrewMemberIds = new Set(
      [prevCrew?.projectManager, prevCrew?.siteLead, ...(prevCrew?.members ?? [])]
        .filter((m): m is NonNullable<typeof m> => !!m?.id)
        .map((m) => removeAuth0Prefix(m.id))
    )
    const assignedEntries = Object.entries(target.assignedUsers ?? {}).filter(([, v]) => v.assigned)
    const hasCustomSelections = assignedEntries.some(([id]) => !prevCrewMemberIds.has(id))

    const buildNewAssignedUsers = (): CalendarEvent['assignedUsers'] => {
      const newCrew = allCrews.find((c) => c._id === crewId)
      if (!newCrew) return undefined
      const owners = [newCrew.projectManager, newCrew.siteLead, ...(newCrew.members ?? [])].filter(
        (m): m is NonNullable<typeof m> => !!m?.id
      )
      if (owners.length === 0) return undefined
      return Object.fromEntries(
        owners.map((m) => {
          const name = [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || undefined
          return [removeAuth0Prefix(m.id), { assigned: true as const, ...(name ? { name } : {}) }]
        })
      )
    }

    if (!hasCustomSelections) {
      updateEvent({ ...target, crew: crewId, assignedUsers: buildNewAssignedUsers() })
      if (closeOnCrewChange) onClose()
      return
    }

    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message:
        'Changing the crew will replace the selected members with the new crew’s defaults. Any custom (unassigned) users or members from other crews you added will be removed.',
      onConfirm: () => {
        updateEvent({ ...target, crew: crewId, assignedUsers: buildNewAssignedUsers() })
        dismissDialog()
        if (closeOnCrewChange) onClose()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  const handleProgressChange = (target: CalendarEvent, progress: number) => {
    updateEvent({
      ...target,
      progress,
      previousProgress: target.progress ?? 0
    })
  }

  const handleDelete = (target: CalendarEvent) => {
    onClose()
    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: 'Are you sure you want to delete this job? This action cannot be undone.',
      onConfirm: () => {
        deleteEvent(target)
        dismissAllDialogs()
      },
      onCancel: dismissDialog
    })
  }

  return (
    <EventPreviewPopper
      event={event}
      forceProduction
      anchorEl={anchorEl ?? null}
      setAnchorEl={() => {}}
      users={[]}
      onClose={onClose}
      onEditEvent={handleEdit}
      onCancelEvent={handleDelete}
      onProgressChange={handleProgressChange}
      onCrewChange={handleCrewChange}
      onNavigate={(path) => history.push(path)}
      readOnly={!permissionToEdit}
    />
  )
}
